/**
 * @module ReasoningErrorState
 * @purpose Error state component for the reasoning panel — shows failure message and retry.
 * @depends react-i18next, lucide-react
 * @dependedOnBy CanvasReasoningPanel
 *
 * @rules
 * - Performance: pure display — no data fetching
 * - State: none
 * - Errors: uses role="alert" for screen readers
 */

import { useTranslation } from 'react-i18next'
import { AlertCircle } from 'lucide-react'

interface ReasoningErrorStateProps {
  onRetry: () => void
}

/**
 * Renders the reasoning panel error state with caution icon and retry button.
 *
 * @behaviour Uses role="alert" for screen readers.
 *            Calls onRetry when the retry button is clicked.
 * @errors n/a — purely presentational
 */
export function ReasoningErrorState({ onRetry }: ReasoningErrorStateProps): React.ReactElement {
  const { t } = useTranslation('analysis')
  return (
    <div role="alert" className="flex flex-col items-center gap-2 px-4 py-8">
      <AlertCircle className="h-5 w-5" style={{ color: 'var(--text-muted)' }} />
      <p className="text-sm" style={{ color: 'var(--text)' }}>
        {t('reasoning.error.title')}
      </p>
      <button
        type="button"
        onClick={onRetry}
        className="text-xs uppercase tracking-wide underline hover:no-underline"
        style={{ fontFamily: 'var(--font-mono)', color: 'var(--accent)' }}
      >
        {t('reasoning.error.retry')}
      </button>
    </div>
  )
}
